/**
 * The match setup lives in the URL (`/play/match?mech=&opponent=&bot=&seed=...`), so a match is
 * reproducible from its address and the results overlay can rematch without reloading assets.
 */
import { arenaForSeed, DEFAULT_ARENA_ID, isArenaId, type ArenaId } from '@steelwake/shared/arena';
import type { BotLevel } from '@steelwake/shared/combat';
import { MECH_LIST, MECHS } from '@steelwake/shared/data';

export interface MatchParams {
  /** The arena (`?arena=`; `random` draws one from the seed, absent means the Foundry). */
  arena: ArenaId;
  mech: string;
  opponent: string;
  bot: BotLevel;
  seed: number;
  roundSeconds: number;
  countdownSeconds: number;
  roundsToWin: number;
  /** Practice cheat (needs `e2e=1`): start with the ultimate charged. */
  ultReady: boolean;
}

export const BOT_LEVELS: readonly BotLevel[] = ['idle', 'easy', 'normal', 'hard'];
export const DEFAULT_MECH = 'warden';

function mechId(value: string | null, fallback: string): string {
  return value !== null && value in MECHS ? value : fallback;
}

function integer(value: string | null, min: number, max: number, fallback: number): number {
  const n = Number(value);
  if (value === null || !Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, Math.round(n)));
}

/** A seed for a fresh match: deterministic once written into the URL. */
export function freshSeed(now = Date.now()): number {
  return (now ^ Math.floor(Math.random() * 0x7fffffff)) & 0x7fffffff;
}

/** Any opponent but the player's own mech, chosen by the seed so the URL stays reproducible. */
export function opponentForSeed(seed: number, mech: string): string {
  const others = MECH_LIST.filter((m) => m.id !== mech);
  const pick = others[seed % others.length] ?? others[0];
  return pick?.id ?? DEFAULT_MECH;
}

export function parseMatchParams(search: string, now = Date.now()): MatchParams {
  const q = new URLSearchParams(search);
  const seed = integer(q.get('seed'), 0, 0x7fffffff, freshSeed(now));
  const mech = mechId(q.get('mech'), DEFAULT_MECH);
  const opponentRaw = q.get('opponent');
  const opponent =
    opponentRaw === null || opponentRaw === 'random'
      ? opponentForSeed(seed, mech)
      : mechId(opponentRaw, opponentForSeed(seed, mech));
  const botRaw = q.get('bot');
  const bot: BotLevel =
    botRaw !== null && (BOT_LEVELS as string[]).includes(botRaw) ? (botRaw as BotLevel) : 'normal';
  const arenaRaw = q.get('arena');
  const arena: ArenaId =
    arenaRaw === 'random'
      ? arenaForSeed(seed)
      : arenaRaw !== null && isArenaId(arenaRaw)
        ? arenaRaw
        : DEFAULT_ARENA_ID;
  return {
    arena,
    mech,
    opponent,
    bot,
    seed,
    roundSeconds: integer(q.get('roundSeconds'), 5, 600, 180),
    countdownSeconds: integer(q.get('countdown'), 0, 30, 10),
    roundsToWin: integer(q.get('rounds'), 1, 5, 2),
    ultReady: q.get('ult') === 'ready',
  };
}

/** Query string for a setup (keeps unrelated flags such as `e2e` and `preset`). */
export function matchSearch(params: MatchParams, base = ''): string {
  const q = new URLSearchParams(base);
  if (params.arena !== DEFAULT_ARENA_ID) q.set('arena', params.arena);
  else q.delete('arena');
  q.set('mech', params.mech);
  q.set('opponent', params.opponent);
  q.set('bot', params.bot);
  q.set('seed', String(params.seed));
  if (params.roundSeconds !== 180) q.set('roundSeconds', String(params.roundSeconds));
  else q.delete('roundSeconds');
  if (params.countdownSeconds !== 10) q.set('countdown', String(params.countdownSeconds));
  else q.delete('countdown');
  if (params.roundsToWin !== 2) q.set('rounds', String(params.roundsToWin));
  else q.delete('rounds');
  if (params.ultReady) q.set('ult', 'ready');
  else q.delete('ult');
  return `?${q.toString()}`;
}
